import React, {Component} from 'react';
import {Row, Table, Button, Modal, Form} from 'react-bootstrap';
import {connect} from 'react-redux';
import {useFilters, useSortBy, useTable} from 'react-table';
import {apiRequest} from '../../../../../../functions/api';
import {pushNotification} from '../../../../../../functions/notifications';
import {getDeepState, setDeepState} from '../../../../../../functions/react.utilities';
import moment from 'moment';

const DefaultColumnFilter = ({column: {filterValue, setFilter, Header}}) => {
    return (
        <input
            value={filterValue || ''}
            onChange={e => setFilter(e.target.value || undefined)}
            placeholder={`Filtrer...`}
            className="form-control form-control-sm"
            style={{marginTop: '5px'}}
        />
    );
};

const WaitlistTable = ({data, isLoading, clearSelectionRef, onSelectionChange}) => {
    const [selectedRows, setSelectedRows] = React.useState([]);

    React.useEffect(() => {
        if (clearSelectionRef) {
            clearSelectionRef.current = () => setSelectedRows([]);
        }
    }, [clearSelectionRef]);

    React.useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selectedRows);
        }
    }, [selectedRows, onSelectionChange]);

    const toggleRow = (id) => {
        setSelectedRows(prev => {
            if (prev.includes(id)) {
                return prev.filter(rowId => rowId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const toggleAll = () => {
        if (selectedRows.length === data.length) {
            setSelectedRows([]);
        } else {
            setSelectedRows(data.map(row => row.id));
        }
    };


    const columns = React.useMemo(
        () => [
            {
                id: 'selection',
                Header: ({data}) => (
                    <input
                        type="checkbox"
                        checked={selectedRows.length === data.length && data.length > 0}
                        onChange={toggleAll}
                    />
                ),
                Cell: ({row}) => (
                    <input
                        type="checkbox"
                        checked={selectedRows.includes(row.original.id)}
                        onChange={() => toggleRow(row.original.id)}
                    />
                ),
                disableFilters: true,
                disableSortBy: true,
            },
            {
                Header: 'Nom',
                accessor: 'lastName',
                Filter: DefaultColumnFilter,
            },
            {
                Header: 'Prénom',
                accessor: 'firstName',
                Filter: DefaultColumnFilter,
            },
            {
                Header: 'Email',
                accessor: 'email',
                Filter: DefaultColumnFilter,
                Cell: ({value}) => (
                    <a href={`mailto:${value}`} target="_blank" rel="noopener noreferrer">{value}</a>
                )
            },
            {
                Header: 'Téléphone',
                accessor: 'phone',
                Filter: DefaultColumnFilter,
                Cell: ({value}) => value ? (
                    <a href={`tel:${value}`}>{value}</a>
                ) : '-'
            },
            {
                Header: 'Date d\'inscription',
                accessor: 'registrationDate',
                Filter: DefaultColumnFilter,
                Cell: ({value}) => value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
                sortType: 'customDateSort'
            }
        ],
        [selectedRows, data]
    );

    const defaultColumn = React.useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        []
    );

    const sortTypes = React.useMemo(
        () => ({
            customDateSort: (rowA, rowB, columnId) => {
                const dateA = moment(rowA.values[columnId]);
                const dateB = moment(rowB.values[columnId]);

                if (!dateA.isValid()) return 1;
                if (!dateB.isValid()) return -1;

                return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
            }
        }),
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            sortTypes,
            initialState: {
                sortBy: [{id: 'registrationDate', desc: true}]
            }
        },
        useFilters,
        useSortBy
    );

    if (isLoading) {
        return (
            <div className="text-center p-4">
                <div className="spinner-border" role="status">
                    <span className="sr-only">Chargement...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="table-responsive">
            <Table {...getTableProps()} className="table">
                <thead>
                {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                            <th {...column.getHeaderProps()}>
                                <div {...column.getSortByToggleProps()}>
                                    {column.render('Header')}
                                    <span>
                                            {column.isSorted
                                                ? column.isSortedDesc
                                                    ? <i className="mdi mdi-sort-alphabetical-descending" />
                                                    : <i className="mdi mdi-sort-alphabetical-ascending" />
                                                : ''}
                                        </span>
                                </div>
                                <div>{column.canFilter ? column.render('Filter') : null}</div>
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {rows.length === 0 ? (
                    <tr>
                        <td colSpan={columns.length} className="text-center">
                            Aucune inscription en liste d'attente
                        </td>
                    </tr>
                ) : (
                    rows.map(row => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map(cell => {
                                    return (
                                        <td {...cell.getCellProps()}>
                                            {cell.render('Cell')}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })
                )}
                </tbody>
            </Table>
        </div>
    );
};

class WaitListTab extends Component {
    constructor(props) {
        super(props);
        this.setDeepState = setDeepState(this);
        this.getDeepState = getDeepState(this);
        this._isMounted = false;
        this.clearSelectionRef = React.createRef();

        this.state = {
            edit: false,
            isValid: false,
            nbRegistrantsMax: this.getEvent().nbRegistrantsMax,
            waitlistData: [],
            isLoading: false,
            showDeleteModal: false,
            selectedIds: [],
            showAddModal: false,
            newEntry: {
                lastName: '',
                firstName: '',
                email: '',
                phone: ''
            },
            validationErrors: {
                lastName: '',
                firstName: '',
                email: '',
                phone: ''
            },
            touched: {
                lastName: false,
                firstName: false,
                email: false,
                phone: false
            }
        }

        this.loadWaitlist = this.loadWaitlist.bind(this);
        this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
        this.handleDeleteCancel = this.handleDeleteCancel.bind(this);
        this.handleSelectionChange = this.handleSelectionChange.bind(this);
        this.handleAddOpen = this.handleAddOpen.bind(this);
        this.handleAddCancel = this.handleAddCancel.bind(this);
        this.handleAddConfirm = this.handleAddConfirm.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.validateField = this.validateField.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
        this.loadWaitlist();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    getEvent() {
        return {...this.props.sejours.events[this.props.EID]};
    }

    loadWaitlist() {
        if (!this._isMounted) return;

        this.setState({isLoading: true});
        apiRequest({
            url: '/sejours.events.waitlist/get',
            data: {
                EID: this.props.EID
            }
        })
            .then((res) => {
                if (!this._isMounted) return;

                if (res.data.http_code === 200 && res.data.status === "success") {
                    this.setState({
                        waitlistData: res.data.data?.waitlist || [],
                        isLoading: false
                    });
                } else {
                    pushNotification({
                        type: "error",
                        title: "Erreur",
                        icon: "mdi mdi-close",
                        content: <p>Erreur lors du chargement de la liste d'attente</p>
                    });
                    this.setState({isLoading: false});
                }
            })
            .catch((error) => {
                if (!this._isMounted) return;

                pushNotification({
                    type: "error",
                    title: "Erreur",
                    icon: "mdi mdi-close",
                    content: <p>Erreur lors du chargement de la liste d'attente</p>
                });
                this.setState({isLoading: false});
            });
    }

    handleSelectionChange(selectedIds) {
        this.setState({selectedIds});
    }

    handleDeleteCancel() {
        if (this.clearSelectionRef.current) {
            this.clearSelectionRef.current();
        }
        this.setState({
            showDeleteModal: false,
            selectedIds: []
        });
    }

    handleDeleteConfirm() {
        const {selectedIds} = this.state;

        if (!this._isMounted || selectedIds.length === 0) return;

        this.setState({isLoading: true, showDeleteModal: false});

        apiRequest({
            url: '/sejours.events.waitlist/delete',
            data: {
                EID: this.props.EID,
                ids: selectedIds
            }
        })
            .then((res) => {
                if (!this._isMounted) return;

                if (res.data.http_code === 200 && res.data.status === "success") {
                    pushNotification({
                        type: "success",
                        title: "Succès",
                        icon: "mdi mdi-check",
                        content: <p>Les entrées ont été supprimées de la liste d'attente</p>
                    });
                    if (this.clearSelectionRef.current) {
                        this.clearSelectionRef.current();
                    }
                    this.setState({selectedIds: []});
                    this.loadWaitlist();
                } else {
                    pushNotification({
                        type: "error",
                        title: "Erreur",
                        icon: "mdi mdi-close",
                        content: <p>Erreur lors de la suppression des entrées</p>
                    });
                    this.setState({isLoading: false, selectedIds: []});
                }
            })
            .catch((error) => {
                if (!this._isMounted) return;

                pushNotification({
                    type: "error",
                    title: "Erreur",
                    icon: "mdi mdi-close",
                    content: <p>Erreur lors de la suppression des entrées</p>
                });
                this.setState({isLoading: false, selectedIds: []});
            });
    }

    handleAddOpen() {
        this.setState({
            showAddModal: true,
            newEntry: {
                lastName: '',
                firstName: '',
                email: '',
                phone: ''
            }
        });
    }

    handleAddCancel() {
        this.setState({
            showAddModal: false,
            newEntry: {
                lastName: '',
                firstName: '',
                email: '',
                phone: ''
            },
            validationErrors: {
                lastName: '',
                firstName: '',
                email: '',
                phone: ''
            },
            touched: {
                lastName: false,
                firstName: false,
                email: false,
                phone: false
            }
        });
    }

    validateField(field, value) {
        let error = '';

        switch (field) {
            case 'lastName':
                if (!value || value.trim() === '') {
                    error = 'Le nom est requis';
                } else if (value.trim().length < 2) {
                    error = 'Le nom doit contenir au moins 2 caractères';
                }
                break;
            case 'firstName':
                if (!value || value.trim() === '') {
                    error = 'Le prénom est requis';
                } else if (value.trim().length < 2) {
                    error = 'Le prénom doit contenir au moins 2 caractères';
                }
                break;
            case 'email':
                if (!value || value.trim() === '') {
                    error = 'L\'email est requis';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    error = 'L\'email n\'est pas valide';
                }
                break;
            case 'phone':
                if (!value || value.trim() === '') {
                    error = 'Le téléphone est requis';
                } else if (!/^(?:(?:\+|00)33|0)[1-9](?:[0-9]{2}){4}$/.test(value.replace(/[\s.-]/g, ''))) {
                    error = 'Le numéro de téléphone n\'est pas valide';
                }
                break;
            default:
                break;
        }

        return error;
    }

    handleBlur(field) {
        this.setState({
            touched: {
                ...this.state.touched,
                [field]: true
            }
        });
    }

    handleInputChange(field, value) {
        const error = this.validateField(field, value);

        this.setState({
            newEntry: {
                ...this.state.newEntry,
                [field]: value
            },
            validationErrors: {
                ...this.state.validationErrors,
                [field]: error
            }
        });
    }

    handleAddConfirm() {
        const {newEntry} = this.state;

        // Valider tous les champs
        const errors = {
            lastName: this.validateField('lastName', newEntry.lastName),
            firstName: this.validateField('firstName', newEntry.firstName),
            email: this.validateField('email', newEntry.email),
            phone: this.validateField('phone', newEntry.phone)
        };

        // Marquer tous les champs comme touchés
        const allTouched = {
            lastName: true,
            firstName: true,
            email: true,
            phone: true
        };

        // Vérifier s'il y a des erreurs
        const hasErrors = Object.values(errors).some(error => error !== '');

        if (hasErrors) {
            this.setState({
                validationErrors: errors,
                touched: allTouched
            });
            return;
        }

        if (!this._isMounted) return;

        this.setState({isLoading: true});

        apiRequest({
            url: '/sejours.events.waitlist/post',
            data: {
                EID: this.props.EID,
                lastName: newEntry.lastName,
                firstName: newEntry.firstName,
                email: newEntry.email,
                phone: newEntry.phone
            }
        })
            .then((res) => {
                if (!this._isMounted) return;

                if (res.data.http_code === 200 && res.data.status === "success") {
                    pushNotification({
                        type: "success",
                        title: "Succès",
                        icon: "mdi mdi-check",
                        content: <p>L'entrée a été ajoutée à la liste d'attente</p>
                    });
                    this.setState({
                        showAddModal: false,
                        isLoading: false,
                        newEntry: {
                            lastName: '',
                            firstName: '',
                            email: '',
                            phone: ''
                        },
                        validationErrors: {
                            lastName: '',
                            firstName: '',
                            email: '',
                            phone: ''
                        },
                        touched: {
                            lastName: false,
                            firstName: false,
                            email: false,
                            phone: false
                        }
                    });
                    this.loadWaitlist();
                } else {
                    // Gérer les erreurs de validation de l'API
                    if (res.data.errors) {
                        const apiErrors = {
                            lastName: '',
                            firstName: '',
                            email: '',
                            phone: ''
                        };

                        // Mapper les erreurs de l'API
                        if (res.data.errors.email === 'INVALID') {
                            apiErrors.email = 'L\'email n\'est pas valide';
                        }
                        if (res.data.errors.firstName === 'INVALID_LENGTH') {
                            apiErrors.firstName = 'Le prénom doit contenir entre 1 et 255 caractères';
                        }
                        if (res.data.errors.lastName === 'INVALID_LENGTH') {
                            apiErrors.lastName = 'Le nom doit contenir entre 1 et 255 caractères';
                        }
                        if (res.data.errors.phone === 'INVALID_LENGTH') {
                            apiErrors.phone = 'Le téléphone doit contenir entre 1 et 50 caractères';
                        }
                        if (res.data.errors.phone === 'INVALID_FORMAT') {
                            apiErrors.phone = 'Le format du téléphone n\'est pas valide';
                        }

                        this.setState({
                            isLoading: false,
                            validationErrors: apiErrors,
                            touched: allTouched
                        });
                    } else {
                        pushNotification({
                            type: "error",
                            title: "Erreur",
                            icon: "mdi mdi-close",
                            content: <p>Erreur lors de l'ajout à la liste d'attente</p>
                        });
                        this.setState({isLoading: false});
                    }
                }
            })
            .catch((error) => {
                if (!this._isMounted) return;

                pushNotification({
                    type: "error",
                    title: "Erreur",
                    icon: "mdi mdi-close",
                    content: <p>Erreur lors de l'ajout à la liste d'attente</p>
                });
                this.setState({isLoading: false});
            });
    }

    render() {
        return (
            <React.Fragment>
                <Row>
                    <div className="col-7">
                        <h5>Liste d'attente de l'évènement</h5>
                    </div>
                    <div className="col-5 btn-container-inline-right wrap">
                        {this.state.selectedIds.length > 0 && (
                            <Button size="sm" variant="danger" onClick={() => this.setState({showDeleteModal: true})}>
                                <i className="mdi mdi-delete" />
                                Supprimer ({this.state.selectedIds.length})
                            </Button>
                        )}
                        <Button size="sm" variant="success" onClick={this.handleAddOpen}>
                            <i className="mdi mdi-plus" />
                            Ajouter
                        </Button>
                        <Button size="sm" variant="primary" onClick={this.loadWaitlist} disabled={this.state.isLoading}>
                            <i className="mdi mdi-refresh" />
                            Actualiser
                        </Button>
                    </div>
                </Row>
                <WaitlistTable
                    data={this.state.waitlistData}
                    isLoading={this.state.isLoading}
                    clearSelectionRef={this.clearSelectionRef}
                    onSelectionChange={this.handleSelectionChange}
                />

                <Modal show={this.state.showDeleteModal} onHide={this.handleDeleteCancel}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirmation de suppression</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Êtes-vous sûr de vouloir supprimer {this.state.selectedIds.length} élément(s) de la liste d'attente ?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.handleDeleteCancel}>
                            Annuler
                        </Button>
                        <Button variant="danger" onClick={this.handleDeleteConfirm}>
                            Supprimer
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal show={this.state.showAddModal} onHide={this.handleAddCancel}>
                    <Modal.Header closeButton>
                        <Modal.Title>Ajouter une personne à la liste d'attente</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={this.state.newEntry.lastName}
                                    onChange={(e) => this.handleInputChange('lastName', e.target.value)}
                                    onBlur={() => this.handleBlur('lastName')}
                                    placeholder="Nom"
                                    isInvalid={this.state.touched.lastName && !!this.state.validationErrors.lastName}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {this.state.validationErrors.lastName}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Prénom <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={this.state.newEntry.firstName}
                                    onChange={(e) => this.handleInputChange('firstName', e.target.value)}
                                    onBlur={() => this.handleBlur('firstName')}
                                    placeholder="Prénom"
                                    isInvalid={this.state.touched.firstName && !!this.state.validationErrors.firstName}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {this.state.validationErrors.firstName}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="email"
                                    value={this.state.newEntry.email}
                                    onChange={(e) => this.handleInputChange('email', e.target.value)}
                                    onBlur={() => this.handleBlur('email')}
                                    placeholder="email@example.com"
                                    isInvalid={this.state.touched.email && !!this.state.validationErrors.email}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {this.state.validationErrors.email}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Téléphone <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="tel"
                                    value={this.state.newEntry.phone}
                                    onChange={(e) => this.handleInputChange('phone', e.target.value)}
                                    onBlur={() => this.handleBlur('phone')}
                                    placeholder="06 12 34 56 78"
                                    isInvalid={this.state.touched.phone && !!this.state.validationErrors.phone}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {this.state.validationErrors.phone}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.handleAddCancel}>
                            Annuler
                        </Button>
                        <Button variant="success" onClick={this.handleAddConfirm} disabled={this.state.isLoading}>
                            <i className="mdi mdi-plus"></i> Ajouter
                        </Button>
                    </Modal.Footer>
                </Modal>
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state) => ({sejours: state.sejours});

export default connect(mapStateToProps, null)(WaitListTab);